import React, { useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import s from './styles/StepRecipients.module.scss'
// import { ReactComponent as PeopleIcon } from 'assets/icons/people.svg'
// import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as SearchIcon } from 'assets/icons/search_empty.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as ArrowLeftIcon } from 'assets/icons/arrowLeft.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import InitialAvatar from 'shared/components/InitialAvatar/InitialAvatar'
import { DataTable } from 'shared/components/DataTable/DataTable'
import { useStepRecipients } from './hooks/useStepRecipients'

const StepRecipients = observer(
	({
		selectedStudents = [],
		savedLists = [],
		activeGrade = 'all',
		onSelectList,
	}) => {
		const {
			searchQuery,
			handleSearchChange,
			showTableView,
			setShowTableViewFalse,
			setShowTableViewTrue,
			filteredStudents,
			showAutocomplete,
			tabs,
			activeTab,
			handleSelectTab,
			isStudentSelected,
			handleToggleStudent,
			handleRemoveStudent,
			allVisibleSelected,
			handleToggleVisibleSelection,
			displaySelectedStudents,
			hasMoreSelected,
			moreCount,
			handleClearSelection,
			isMobile,
		} = useStepRecipients({
			selectedStudents,
			savedLists,
			activeGrade,
			onSelectList,
		})

		// Table columns configuration
		const tableColumns = useMemo(
			() => [
				{
					id: 'student',
					label: 'STUDENT',
					stickyLeft: 0,
					width: '300px',
					headerClassName: s.tableHeaderStudent,
					cellClassName: s.tableCellStudent,
					render: row => (
						<div className={s.tableStudent}>
							<InitialAvatar
								name={row?.name}
								size={32}
								avatarKey={row?.avatarKey}
								avatarSource={row?.avatarSource}
								photoUrl={row?.photoUrl}
							/>
							<span className={s.tableStudentName}>{row?.name || '—'}</span>
						</div>
					),
				},
				{
					id: 'grade',
					label: 'GRADE',
					width: '150px',
					render: row => `Grade ${row?.grade || 'N/A'}`,
				},
				{
					id: 'section',
					label: 'SECTION',
					width: '150px',
					render: row => row?.section || '—',
				},
				{
					id: 'action',
					label: 'ACTION',
					width: '100px',
					cellClassName: s.tableCellAction,
					render: row => (
						<button
							type="button"
							className={s.tableRemoveButton}
							onClick={e => {
								e.stopPropagation()
								handleRemoveStudent(row.id)
							}}
						>
							✕
						</button>
					),
				},
			],
			[handleRemoveStudent],
		)

		// Show table view
		if (showTableView) {
			return (
				<div className={s.recipients}>
					{/* Back button */}
					<button
						type="button"
						className={s.backButton}
						onClick={setShowTableViewFalse}
					>
						<span className={s.backButton__icon}>
							<ArrowLeftIcon />
						</span>
						<span className={s.backButton__text}>Back to Students</span>
					</button>

					{/* Table section */}
					<div className={s.tableSection}>
						<div className={s.table_section__header}>
							<h3 className={s.title}>
								Selected Students ({selectedStudents.length})
							</h3>
							<p className={s.subtitle}>
								Review and manage your student selections
							</p>
						</div>
						<div className={s.table_section__wrapper}>
							<DataTable
								variant="light"
								columns={tableColumns}
								rows={selectedStudents}
								stickyHeader={true}
								scrollClassName={s.tableScroll}
								emptyText="No students selected"
							/>
						</div>
					</div>
				</div>
			)
		}

		// Show list view (default)
		return (
			<div className={s.recipients}>
				{/* Top bar with list name */}
				{/* <div className={s.header}>
					<div className={s.header__inner}>
						<div className={s.header__icon}>
							<PeopleIcon />
						</div>
						<span className={s.header__text}>Assigned to: {listLabel}</span>
					</div>
					<button type="button" className={s.edit} onClick={() => onOpenListDropdown?.()}>
						<div className={s.edit__icon}>
							<PenIcon />
						</div>
						<span className={s.edit__text}>Edit</span>
					</button>
				</div> */}

				{/* Search block */}
				<div className={s.search}>
					<div className={s.listBar}>
						<div className={s.listBar__tabs} aria-label="Student lists">
							{tabs.map(tab => {
								const isActive = String(activeTab) === String(tab.id)
								return (
									<button
										key={tab.id}
										type="button"
										className={`${s.listTab} ${isActive ? s.listTab_active : ''}`}
										onClick={() => handleSelectTab(tab.id)}
									>
										<span className={s.listTab__label}>{tab.label}</span>
										{typeof tab.count === 'number' && (
											<span className={s.listTab__count}>{tab.count}</span>
										)}
									</button>
								)
							})}
						</div>
					</div>

					<div className={s.search__inner}>
						<div className={s.search__icon}>
							<SearchIcon />
						</div>
						<input
							type="text"
							className={s.search__input}
							placeholder="Search students..."
							value={searchQuery}
							onChange={handleSearchChange}
						/>
					</div>

					<div className={s.searchMeta}>
						<div className={s.searchMeta__text}>
							select from here or go to students page
						</div>
						<button
							type="button"
							className={s.selectVisible}
							onClick={handleToggleVisibleSelection}
							disabled={!filteredStudents.length}
						>
							{allVisibleSelected ? 'Deselect all' : 'Select all'}
						</button>
					</div>

					{/* Student list: default browse when search empty, or search results when typing */}
					{showAutocomplete && (
						<div className={s.autocomplete}>
							{filteredStudents.map(student => {
								const isSelected = isStudentSelected(student.id)
								return (
									<div
										key={student.id}
										className={`${s.autocomplete__item} ${isSelected ? s.autocomplete__item_selected : ''}`}
										role="button"
										tabIndex={0}
										onClick={() => handleToggleStudent(student)}
										onKeyDown={e => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault()
												handleToggleStudent(student)
											}
										}}
									>
										<div className={s.autocomplete__info}>
											<div className={s.autocomplete__avatar}>
												<InitialAvatar
													name={student.name}
													size={isMobile ? 25 : 32}
													avatarKey={student.avatarKey}
													avatarSource={student.avatarSource}
													photoUrl={student.photoUrl}
												/>
											</div>
											<div className={s.autocomplete__details}>
												<div className={s.autocomplete__name}>
													{student.name || 'Unknown'}
												</div>
												<div className={s.autocomplete__meta}>
													Grade {student.grade || 'N/A'}
													{student.section && ` • Section ${student.section}`}
												</div>
											</div>
										</div>
										<button
											type="button"
											className={`${s.addButton} ${isSelected ? s.active : ''}`}
											onClick={e => {
												e.stopPropagation()
												handleToggleStudent(student)
											}}
											title={isSelected ? 'Deselect' : 'Add'}
										>
											{isSelected ? <CheckIcon /> : <PlusIcon />}
										</button>
									</div>
								)
							})}
						</div>
					)}
				</div>

				{/* Selected students block */}
				<div className={s.selected}>
					<div className={s.selected__header}>
						<span className={s.selected__title}>
							Selected Students ({selectedStudents.length})
						</span>
						<div className={s.selected__actions}>
							{selectedStudents.length > 0 && (
								<>
									<button
										type="button"
										className={s.deselect}
										onClick={handleClearSelection}
									>
										<span className={s.deselect__text}>Deselect all</span>
										<span className={s.deselect__icon}>
											<CrossIcon />
										</span>
									</button>
									<span className={s.selected__separator} aria-hidden="true">
										|
									</span>
								</>
							)}
							<button
								type="button"
								className={s.viewAll}
								onClick={setShowTableViewTrue}
							>
								<span className={s.viewAll__text}>View all</span>
								<span className={s.viewAll__icon}>
									<ArrowLeftIcon />
								</span>
							</button>
						</div>
					</div>
					<div className={s.selected__list}>
						{displaySelectedStudents.map(student => (
							<div key={student.id} className={s.chip}>
								<span className={s.chip__text}>
									{student.name || 'Unknown'}
								</span>
								<button
									type="button"
									className={s.chip__remove}
									onClick={() => handleRemoveStudent(student.id)}
								>
									✕
								</button>
							</div>
						))}
						{hasMoreSelected && (
							<div className={s.chip}>
								<span className={s.chip__text}>+{moreCount}</span>
							</div>
						)}
					</div>
				</div>
			</div>
		)
	},
)

export default StepRecipients
